/* eslint no-mixed-operators: 0 */
/* eslint camelcase: 0 */
/* eslint @typescript-eslint/naming-convention: 0 */

// This class handles all of the webGL rendering

import numeric from 'numeric'
// import array2mat from 'array2mat'
// import transposeMatrix from 'transposeMatrix'
// import svd from 'svd'
import * as THREE from 'three'
import isMobile from 'utils/isMobile'

import CompositeBackground from 'Renderer/CompositeBackground.js'
import { RUG_SUB_LAYER_DELTA } from 'Renderer/constants'
import { getHardwareInfo } from 'utils/getHardwareInfo'

import { getFloorPlane, getFloorIndex } from "Renderer/SceneUtils"

const tmpMat4 = new THREE.Matrix4()
const tmpXAxis = new THREE.Vector3()
const tmpYAxis = new THREE.Vector3()
const tmpZAxis = new THREE.Vector3()

class WallspiceWebgl {
    static createRenderer(canvas) {
        let downrez = 1
        let antialias = true
        let software = false
        let ext_depth = null
        let canvas_width = null
        let canvas_height = null

        const query_canvas = document.createElement('canvas', { width: 4, height: 4 })
        let ctx = query_canvas.getContext('webgl', { failIfMajorPerformanceCaveat: true })
        if (!ctx) {
            software = true
            ctx = query_canvas.getContext('webgl')

            if (!ctx) {
                // failed to find a context, THREE.js fails here too
                return null
            }
        }
        const debugInfo = ctx.getExtension('WEBGL_debug_renderer_info')
        const rendy = ctx.getParameter(ctx.RENDERER)
        const underlyingVendor = ctx.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)
        const underlyingRenderer = ctx.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
        ext_depth = ctx.getExtension('EXT_frag_depth')
        const wgl2QueryTimerExt = ctx.getExtension('EXT_disjoint_timer_query_webgl2')
        const wgl1QueryTimerExt = ctx.getExtension('EXT_disjoint_timer_query')

        if (rendy === 'WebKit WebGL'
            && underlyingVendor === 'Intel Inc.'
            && underlyingRenderer.indexOf('Iris(TM)') >= 0
            && underlyingRenderer.indexOf('No Disable AA as bug has gone for now') >= 0
            && navigator.platform === 'MacIntel'
            && navigator.userAgent.indexOf('Chrome') >= 0) {
            antialias = false
        }

        // Disabling GPU acceleration in chrome results in usage of SwiftShader implementation of WebGL. 
        // However, context creation with 'failIfMajorPerformanceCaveat' produces a valid context and does not let us know it is software.
        if (rendy === 'WebKit WebGL'
            && underlyingVendor === 'Google Inc. (Google)'
            && underlyingRenderer.indexOf('SwiftShader Device') >= 0
            && underlyingRenderer.indexOf('SwiftShader driver') >= 0
            && navigator.userAgent.indexOf('Chrome') >= 0) {
            software = true
        }

        if (software) {
            downrez = 2
            antialias = false
        }

        canvas_width = canvas.width / downrez
        canvas_height = canvas.height / downrez

        WallspiceWebgl.applyCustomShaderChunks()
        const renderer = new THREE.WebGLRenderer({
            canvas,
            alpha: true,
            preserveDrawingBuffer: true,
            powerPreference: 'high-performance',
            antialias,
        })
        renderer.shadowMap.enabled = !software
        renderer.shadowMap.type = isMobile() ? THREE.PCFSoftShadowMap : THREE.PCFShadowMap
        renderer.setSize(canvas.width / downrez, canvas.height / downrez)
        renderer.setClearAlpha(0)
        renderer.autoClear = false
        renderer.outputEncoding = THREE.sRGBEncoding
        renderer.localClippingEnabled = true
        renderer.toneMappingExposure = 1.0
        const hwinfo = getHardwareInfo()
        renderer.wallspiceContextInfo = {
            hasFragmentDepth: !!ext_depth,
            antialias,
            software,
            canvas_height,
            canvas_width,
            hasWGL1QueryTimer: !!wgl1QueryTimerExt,
            hasWGL2QueryTimer: !!wgl2QueryTimerExt,
            hwinfo
        }
        return renderer
    }

    static applyCustomShaderChunks() {
        if (!THREE.ShaderChunk.envmap_physical_pars_fragment_patched) {
            THREE.ShaderChunk.envmap_physical_pars_fragment = THREE.ShaderChunk.envmap_physical_pars_fragment.replace(
                'vec4 envMapColor = textureCubeUV( envMap, reflectVec, roughness );',
    
                `
                    vec4 envMapColor = textureCubeUV( envMap, envMapRotation * reflectVec, roughness );
                `
            );
            THREE.ShaderChunk.envmap_physical_pars_fragment = THREE.ShaderChunk.envmap_physical_pars_fragment.replace(
                'vec4 envMapColor = textureCubeUV( envMap, worldNormal, 1.0 );',
    
                `
                    vec4 envMapColor = textureCubeUV( envMap, envMapRotation * worldNormal, roughness );
                `
            );
            THREE.ShaderChunk.envmap_common_pars_fragment = THREE.ShaderChunk.envmap_common_pars_fragment.replace(
                'uniform float envMapIntensity;',

                `
                    uniform mat3 envMapRotation;
                    uniform float envMapIntensity;
                `
            )
            THREE.ShaderLib.physical.uniforms = THREE.UniformsUtils.merge([
                THREE.ShaderLib.physical.uniforms,
                { envMapRotation: { value: new THREE.Matrix3().set(0, 0, 1, 0, 1, 0, -1, 0, 0).transpose() } }, // -Z
            ]);
            THREE.ShaderChunk.envmap_physical_pars_fragment_patched = true
        }
    }

    static queryContextInfo(wglrend) {
        if (!wglrend) { return null }
        const ctx = wglrend.getContext()
        const debugInfo = ctx.getExtension('WEBGL_debug_renderer_info')
        const version = ctx.getParameter(ctx.VERSION)
        const GLSLversion = ctx.getParameter(ctx.SHADING_LANGUAGE_VERSION)
        const vendor = ctx.getParameter(ctx.VENDOR)
        const renderer = ctx.getParameter(ctx.RENDERER)
        const underlyingVendor = ctx.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)
        const underlyingRenderer = ctx.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)

        return {
            ...wglrend.wallspiceContextInfo,
            version,
            GLSLversion,
            vendor,
            renderer,
            underlyingVendor,
            underlyingRenderer,
            devicePixelRatio: window.devicePixelRatio,
        }
    }

    static createScene() {
        return new THREE.Scene()
    }

    static createCamera(intrinsics, width, height) {
        const camera = new THREE.PerspectiveCamera()
        const n = 0.2 // Near
        const f = 30 // Far

        const fx = intrinsics[0]
        const fy = intrinsics[1]
        const px = intrinsics[2]
        const py = intrinsics[3]

        camera.left = (-px / fx) * n
        camera.right = ((width - px) / fx) * n
        camera.top = (py / fy) * n
        camera.bottom = ((-height + py) / fy) * n

        camera.near = n
        camera.far = f

        // eslint-disable-next-line func-names
        camera.updateProjectionMatrix = (function() {
            let { left, right, top, bottom } = this

            // support threejs' view offsets used for TAA:
            if ( this.view !== null && this.view.enabled ) {
                left += this.view.offsetX * (right - left) / this.view.fullWidth;
                right += this.view.offsetX * (right - left) / this.view.fullWidth;
                top -= this.view.offsetY * (top - bottom) / this.view.fullHeight;
                bottom -= this.view.offsetY * (top - bottom) / this.view.fullHeight;
            }

            const A = (right + left) / (right - left)
            const B = (top + bottom) / (top - bottom)
            const C = -(this.far + this.near) / (this.far - this.near)
            const D = -(2 * this.far * this.near) / (this.far - this.near)

            this.projectionMatrix.set(
                2 * n / (this.right - this.left), 0, A, 0,
                0, 2 * n / (this.top - this.bottom), B, 0,
                0, 0, C, D,
                0, 0, -1, 0,
            )
        }).bind(camera)
        camera.updateProjectionMatrix()

        return camera
    }

    static hexgammacol(color) {
        const gamma = 1.0
        const r = 255.0 * ((color[0] / 255.0) ** (1.0 / gamma))
        const g = 255.0 * ((color[1] / 255.0) ** (1.0 / gamma))
        const b = 255.0 * ((color[2] / 255.0) ** (1.0 / gamma))

        // eslint-disable-next-line no-bitwise
        return ((r << 16) + (g << 8) + b)
    }

    static createAmbientColoredLight(amb) {
        // eslint-disable-next-line no-bitwise
        const light = new THREE.AmbientLight(new THREE.Color(0.0, 0.0, 0.0), amb.intensity)
        light.originalColor = new THREE.Color(WallspiceWebgl.hexgammacol(amb.color))
        light.originalIntensity = light.intensity
        light.layers.enableAll()
        return light
    }

    static createReducedAmbientColoredLight(amb) {
        // eslint-disable-next-line no-bitwise
        const light = new THREE.AmbientLight(new THREE.Color(0.0, 0.0, 0.0), amb.intensity * 0.1)
        light.originalColor = new THREE.Color(WallspiceWebgl.hexgammacol(amb.color))
        light.originalIntensity = light.intensity
        light.layers.enableAll()
        return light
    }

    static createPointColoredLight(light, shadow, mobileTextureSize) {
        const pLight = new THREE.PointLight(new THREE.Color(0.0, 0.0, 0.0), light.intensity)
        pLight.originalColor = new THREE.Color(WallspiceWebgl.hexgammacol(light.color))
        pLight.originalIntensity = light.intensity
        pLight.frustumCulled = false
        pLight.position.set(light.position[0], -light.position[1], light.position[2])
        pLight.layers.enableAll()

        pLight.shadow.bias = -0.00014
        pLight.shadow.mapSize.width = isMobile() ? mobileTextureSize : 2048
        pLight.shadow.mapSize.height = isMobile() ? mobileTextureSize : 2048
        pLight.shadow.radius = 2.0
        pLight.shadow.camera.near = 0.1
        pLight.shadow.camera.far = 50.0
        pLight.shadow.camera.frustumCulled = false
        if (shadow) {
            pLight.castShadow = true
        }
        return pLight
    }

    static createDirectionalColoredLight(light, shadow, mobileTextureSize) {
        const dirLight = new THREE.DirectionalLight(new THREE.Color(0.0, 0.0, 0.0), light.intensity)
        dirLight.originalColor = new THREE.Color(WallspiceWebgl.hexgammacol(light.color))
        dirLight.originalIntensity = light.intensity
        dirLight.frustumCulled = false
        dirLight.position.set(light.direction[0] * 5.0, -light.direction[1] * 5.0, light.direction[2] * 5.0)
        dirLight.layers.enableAll()

        dirLight.shadow.bias = -0.00002
        dirLight.shadow.normalBias = 0.04
        dirLight.shadow.mapSize.width = isMobile() ? mobileTextureSize : 2048
        dirLight.shadow.mapSize.height = isMobile() ? mobileTextureSize : 2048
        dirLight.shadow.radius = 3.0
        dirLight.shadow.camera.near = -10.0
        dirLight.shadow.camera.far = 100
        dirLight.shadow.camera.left = -10
        dirLight.shadow.camera.right = 10
        dirLight.shadow.camera.bottom = -10
        dirLight.shadow.camera.top = 10
        dirLight.shadow.camera.frustumCulled = false
        if (shadow) {
            dirLight.castShadow = true
        }
        return dirLight
    }

    static createAmbientLight(brightness) {
        const light = new THREE.AmbientLight(0xffffff, brightness)
        light.originalIntensity = light.intensity
        light.layers.enableAll()
        return light
    }

    static createPointLight(vector, brightness) {
        // eslint-disable-next-line no-bitwise
        const pLight = new THREE.PointLight((243 << 16) + (241 << 8) + 234, brightness)
        pLight.originalIntensity = brightness
        const { x, y, z } = vector
        pLight.frustumCulled = false
        pLight.position.set(x, -y, z)
        pLight.layers.enableAll()
        return pLight
    }

    static createDirectionalLight(vector, brightness, shadow, mobileTextureSize) {
        const dirLight = new THREE.DirectionalLight(0xffffff, brightness)
        dirLight.originalIntensity = brightness
        const { x, y, z } = vector
        dirLight.frustumCulled = false
        dirLight.position.set(x, -y, z)
        dirLight.layers.enableAll()

        dirLight.shadow.bias = -0.00002
        dirLight.shadow.normalBias = 0.02
        dirLight.shadow.mapSize.width = isMobile() ? mobileTextureSize : 2048
        dirLight.shadow.mapSize.height = isMobile() ? mobileTextureSize : 2048
        dirLight.shadow.radius = 3.0
        dirLight.shadow.camera.near = -10.0
        dirLight.shadow.camera.far = 1000
        dirLight.shadow.camera.left = -10
        dirLight.shadow.camera.right = 10
        dirLight.shadow.camera.bottom = -10
        dirLight.shadow.camera.top = 10
        dirLight.shadow.camera.frustumCulled = false
        if (shadow) {
            dirLight.castShadow = true
        }
        return dirLight
    }

    static fitShadowMap(light, sceneCam, depth_occlusion_meta_0) {
        // Only fit directional light shadow maps
        if (!(light instanceof THREE.DirectionalLight))
            return

        // Calculate the shadow camera parameters for improved utilization of the shadow map
        const cameraPos = sceneCam.position
        const cameraFront = sceneCam.getWorldDirection(new THREE.Vector3())
        const lightRotMat = new THREE.Matrix4().lookAt(light.position, new THREE.Vector3(), new THREE.Vector3(0, 1, 0))
        const lightTransMat = new THREE.Matrix4().setPosition(light.position)
        const lightModelMatInv = new THREE.Matrix4().multiplyMatrices(lightTransMat, lightRotMat).invert()
        const currentWidthUnits = light.shadow.camera.right - light.shadow.camera.left
        const currentHeightUnits = light.shadow.camera.top - light.shadow.camera.bottom
        const currentCenter = new THREE.Vector2(
            (light.shadow.camera.right + light.shadow.camera.left) / 2,
            (light.shadow.camera.top + light.shadow.camera.bottom) / 2,
        )
        const lightSpaceNDCProject = v => v.clone().applyMatrix4(lightModelMatInv)
            .applyMatrix4(light.shadow.camera.projectionMatrix)
        light.shadow.camera.updateProjectionMatrix()
        if (depth_occlusion_meta_0) {
            // Use the room's max/min depth values to fit the shadow map
            const p1NDC = lightSpaceNDCProject(cameraPos.clone())
            const p2NDC = lightSpaceNDCProject(cameraPos.clone().add(cameraFront).multiplyScalar(depth_occlusion_meta_0.max))
            const minWidth = Math.abs(p2NDC.x - p1NDC.x)
            const minHeight = Math.abs(p2NDC.y - p1NDC.y)
            const dimensionRatio = light.shadow.mapSize.width / light.shadow.mapSize.height
            const widthNDC = Math.max(minWidth, dimensionRatio * minHeight) * (1.2 + Math.sqrt(2) / 2) // Worst case diagonal padding
            const heightNDC = Math.max(minHeight, (1 / dimensionRatio) * minWidth) * (1.2 + Math.sqrt(2) / 2) // Worst case diagonal padding
            const newRelativeCenterNDC = new THREE.Vector2((p1NDC.x + p2NDC.x) / 2, (p1NDC.y + p2NDC.y) / 2)
            /* eslint-disable no-param-reassign */
            light.shadow.camera.left = (newRelativeCenterNDC.x - widthNDC / 2) * (currentWidthUnits / 2) + currentCenter.x 
            light.shadow.camera.right = (newRelativeCenterNDC.x + widthNDC / 2) * (currentWidthUnits / 2) + currentCenter.x
            light.shadow.camera.bottom = (newRelativeCenterNDC.y - heightNDC / 2) * (currentHeightUnits / 2) + currentCenter.y
            light.shadow.camera.top = (newRelativeCenterNDC.y + heightNDC / 2) * (currentHeightUnits / 2) + currentCenter.y
            /* eslint-enable no-param-reassign */
        } else {
            // Guess the scene's approximate center point using the camera's focal length
            const sceneCenterViewSpace = 0.0008436 * sceneCam.projectionMatrix.elements[0] + 1.501
            const sceneCenter = cameraPos.clone().add(cameraFront).multiplyScalar(sceneCenterViewSpace)
            const sceneCenterLightSpaceNDC = lightSpaceNDCProject(sceneCenter)
            /* eslint-disable no-param-reassign */
            light.shadow.camera.left = sceneCenterLightSpaceNDC.x * (currentWidthUnits / 2) + currentCenter.x - 3.5 
            light.shadow.camera.right = sceneCenterLightSpaceNDC.x * (currentWidthUnits / 2) + currentCenter.x + 3.5
            light.shadow.camera.bottom = sceneCenterLightSpaceNDC.y * (currentHeightUnits / 2) + currentCenter.y - 3.5
            light.shadow.camera.top = sceneCenterLightSpaceNDC.y * (currentHeightUnits / 2) + currentCenter.y + 3.5
            /* eslint-enable no-param-reassign */
        }
        light.shadow.camera.updateProjectionMatrix()
    }

    static createFrameMaterial(fname = `${CDN_BUCKET}/testAssets/frames/textures/test.png`,
            albedo = 0xFFFFFF) {
        return new Promise((resolve) => {
            const loader = new THREE.TextureLoader()
            loader.load(fname, (texture) => {
                const map = texture
                map.anisotropy = 8
                map.needsUpdate = true
                map.wrapS = THREE.RepeatWrapping
                map.wrapT = THREE.RepeatWrapping

                const frameMaterial = new THREE.MeshStandardMaterial({ map, color: albedo })
                // frameMaterial.side = THREE.SingleSide

                resolve(frameMaterial)
            })
        })
    }

    static createArtMaterial(artImage) {
        const map = new THREE.DataTexture(
            new Uint8Array(artImage.data),
            artImage.width,
            artImage.height,
            THREE.RGBAFormat,
            THREE.UnsignedByteType,
            THREE.UVMapping,
            THREE.ClampToEdgeWrapping,
            THREE.ClampToEdgeWrapping,
            THREE.LinearFilter,
            THREE.LinearFilter,
            8,
        )
        map.flipY = true
        map.needsUpdate = true

        const artMaterial = new THREE.MeshStandardMaterial({ map, side: THREE.DoubleSide })

        return artMaterial
    }

    static createDebugSphere(pos, radius, col) {
        const bgM = new THREE.MeshBasicMaterial({ color: col, side: THREE.DoubleSide })
        bgM.transparent = false
        bgM.depthTest = false
        bgM.depthWrite = true

        const sphereGeo = new THREE.SphereGeometry(radius, 12, 12)
        // sphereGeo.translate(pos.x, pos.y, pos.z)
        const intrinsicMesh = new THREE.Mesh(sphereGeo, bgM)
        intrinsicMesh.position.set(pos.x, pos.y, pos.z)

        intrinsicMesh.name = 'debugLight'
        return intrinsicMesh
    }

    static createLightHelper(light, size, col) {
        let Type = null
        if (light instanceof THREE.PointLight) {
            Type = THREE.PointLightHelper
        } else if (light instanceof THREE.DirectionalLight) {
            Type = THREE.DirectionalLightHelper
        }
        return Type
            ? new Type(light, size, col)
            : WallspiceWebgl.createDebugSphere(light.position, size, col);
    }

    static createAppMesh(fd, debug, software) {
        let shadowfloor = null
        if (fd) {
            if (debug === 1) {
                const horizongeo = new THREE.PlaneGeometry(2, 2, 2, 2)
                const material = new THREE.MeshBasicMaterial({ color: 0x00FF00, wireframe: true })
                shadowfloor = new THREE.Mesh(horizongeo, material)
                shadowfloor.name = 'horizon'
            } else if (debug === 2) {
                const geometry = new THREE.BufferGeometry()
                // eslint-disable-next-line no-array-constructor
                const pos = new Array()
                let flip = 1
                const radius = 2.0
                for (let f = 0.0; f <= 2.0 * Math.PI + 0.04; f += Math.PI / 100.0) {
                    pos.push(Math.cos(f) * radius, Math.sin(f) * radius, -10.0 * flip)
                    pos.push(Math.cos(f) * radius, Math.sin(f) * radius, 10.0 * flip)
                    flip *= -1.0
                }
                geometry.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3))
                // geometry.setDrawRange(0, pos.length / 3)

                const material = new THREE.LineBasicMaterial({ color: 0xFF00FF })

                shadowfloor = new THREE.Line(geometry, material)
                shadowfloor.name = 'vertical vanishing lines'
            } else if (debug === 3) {
                const geometry = new THREE.BufferGeometry()
                // eslint-disable-next-line no-array-constructor
                const pos = new Array()
                const radius = 0.25
                for (let f = 0.0; f <= 2.0 * Math.PI + 0.04; f += Math.PI / 50.0) {
                    pos.push(Math.cos(f) * radius, Math.sin(f) * radius, -1.0)
                }
                geometry.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3))
                // geometry.setDrawRange(0, pos.length / 3)

                const material = new THREE.LineBasicMaterial({ color: 0x000000 })

                shadowfloor = new THREE.Line(geometry, material)

                shadowfloor.name = 'principal point ring'
            } else if (debug === 4) {
                const floorgeo = new THREE.PlaneGeometry(50, 50)

                const material = new THREE.MeshBasicMaterial({ color: 0x000000 }) // "none more black"
                material.depthWrite = false

                shadowfloor = new THREE.Mesh(floorgeo, material)

                floorgeo.translate(0, 0, -fd.d)
                shadowfloor.receiveShadow = false

                shadowfloor.name = 'shadow floor black'
            } else if (!software) {
                const floorgeo = new THREE.PlaneGeometry(50, 50)

                const material = new THREE.ShadowMaterial()
                material.opacity = 0.3
                material.depthWrite = false
                material.stencilWrite = true
                material.stencilFunc = THREE.NotEqualStencilFunc
                material.stencilRef = 3

                shadowfloor = new THREE.Mesh(floorgeo, material)

                floorgeo.translate(0, 0, -(fd.d - RUG_SUB_LAYER_DELTA))
                shadowfloor.receiveShadow = true

                shadowfloor.name = 'shadow floor receiver'

                shadowfloor.material.needsUpdate = true
            }
            if (shadowfloor) {
                const shadmat = new THREE.Matrix4()
                WallspiceWebgl.createMat4(fd.rot, [0, 0, 0], shadmat)

                shadowfloor.matrixAutoUpdate = false

                if (debug !== 3) {
                    shadowfloor.matrix.copy(shadmat)
                }
                shadowfloor.needsUpdate = true
            }
        }
        return shadowfloor
    }

    static createTextHelper(string, size, position) {
        const fontSize = size * 10
        const canvas = document.createElement('canvas', { width: string.length * fontSize / 2, height: fontSize })
        const ctx = canvas.getContext('2d')
        ctx.font = `${fontSize}px Monospace`
        ctx.fillText(string, 0, fontSize)

        const textTexture = new THREE.CanvasTexture(canvas)

        const geometry = new THREE.PlaneGeometry(0.2, 0.1)
        const material = new THREE.MeshBasicMaterial({ 
            map: textTexture, 
            side: THREE.DoubleSide, 
            transparent: true,
            depthTest: false,
        })
        const plane = new THREE.Mesh(geometry, material)
        plane.position.copy(position)
        return plane
    }

    static setOpacity(shadowfloor, opacity) {
        if (shadowfloor && shadowfloor.material) {
            // eslint-disable-next-line no-param-reassign
            shadowfloor.material.opacity = opacity;
        }
    }

    static intrinsicsRectGeo(intrinsics, width, height, d = 1) {
        const fx = intrinsics[0]
        const fy = intrinsics[1]
        const px = intrinsics[2]
        const py = intrinsics[3]

        const intrinsicRect = new THREE.PlaneGeometry(
            (width / fx) * d, (-height / fy) * d)
        intrinsicRect.translate(
            d * 0.5 * ((width - px) - px) / fx,
            d * -0.5 * ((height - py) - py) / fy, -1.0 * d)

        return intrinsicRect
    }

    static createBackground(intrinsics, width, height, bgTex) {
        const intrinsicRect = WallspiceWebgl.intrinsicsRectGeo(intrinsics, width, height)

        const bgM = new THREE.ShaderMaterial({
            side: THREE.DoubleSide,
            uniforms: THREE.ShaderLib.basic.uniforms,
            vertexShader: THREE.ShaderLib.basic.vertexShader,
            fragmentShader: THREE.ShaderLib.basic.fragmentShader.replace('#include <tonemapping_fragment>', ''),
        })
        bgM.map = bgTex
        bgM.uniforms.map.value = bgTex
        bgM.uniforms.diffuse.value = new THREE.Color(0xFFFFFF)
        bgM.transparent = false
        bgM.depthTest = false
        bgM.depthWrite = false

        const intrinsicMesh = new THREE.Mesh(intrinsicRect, bgM)

        intrinsicMesh.name = 'background image'
        return intrinsicMesh
    }

    static createBackgroundFromURL(intrinsics, width, height, fgUrl) {
        const intrinsicRect = WallspiceWebgl.intrinsicsRectGeo(intrinsics, width, height)

        const bgM = new THREE.MeshBasicMaterial({ color: 0xFFFFFF, side: THREE.DoubleSide })
        bgM.transparent = false
        bgM.depthTest = false
        bgM.depthWrite = false

        const intrinsicMesh = new THREE.Mesh(intrinsicRect, bgM)

        const tloader = new THREE.TextureLoader()
        // tloader.setPath(this.furnitureData.pathUrl);
        tloader.load(fgUrl, (bgTex) => {
            // eslint-disable-next-line no-param-reassign
            bgTex.flipY = false
            // eslint-disable-next-line no-param-reassign
            bgTex.encoding = THREE.sRGBEncoding
            // eslint-disable-next-line no-param-reassign
            bgTex.needsUpdate = true
            bgM.map = bgTex
            bgM.opacity = 1.0;
            bgM.needsUpdate = true
        })

        intrinsicMesh.name = 'background image'
        return intrinsicMesh
    }

    static createCompositeBackground(renderer, intrinsics, width, height, manifest, panoTexture) {
        return new CompositeBackground(renderer, intrinsics, width, height, manifest, panoTexture)
    }

    static applyMaskTexToMat(tgtmat, tex) {
        // eslint-disable-next-line no-param-reassign
        tgtmat.opacity = 1.0
        // eslint-disable-next-line no-param-reassign
        tgtmat.alphaTest = 0.5
        // eslint-disable-next-line no-param-reassign
        tgtmat.alphaMap = tex
        // eslint-disable-next-line no-param-reassign
        tgtmat.needsUpdate = true
    }

    static createImgPaste(intrinsics, width, height) {
        let intrinsicMesh = null
        const iM = new THREE.MeshBasicMaterial({ color: 0xFFFFFF, side: THREE.DoubleSide })
        iM.transparent = false
        iM.opacity = 1.0; // keep invisible until texload lmbda completes
        iM.depthTest = false
        iM.depthWrite = false

        const intrinsicRect = WallspiceWebgl.intrinsicsRectGeo(intrinsics, width, height)
        intrinsicMesh = new THREE.Mesh(intrinsicRect, iM)

        const pixels = new Uint8Array([255, 0, 0, 0, 255, 0, 0, 0, 0, 255, 255, 255])

        iM.map = new THREE.DataTexture(pixels,
            2,
            2,
            THREE.RGBFormat,
            THREE.UnsignedByteType,
            THREE.UVMapping,
            THREE.ClampToEdgeWrapping,
            THREE.ClampToEdgeWrapping,
            THREE.LinearFilter,
            THREE.LinearFilter,
            2,
        )

        intrinsicMesh.name = 'over image'
        return intrinsicMesh
    }

    static createForeground(intrinsics, width, height, fgUrl) {
        let intrinsicMesh = null
        const fgM = new THREE.MeshBasicMaterial({ color: 0xFFFFFF, side: THREE.DoubleSide })
        fgM.transparent = true
        fgM.alphaTest = 0.75
        fgM.opacity = 0.0; // keep invisible until texload lmbda completes
        fgM.depthTest = false
        fgM.depthWrite = false

        const intrinsicRect = WallspiceWebgl.intrinsicsRectGeo(intrinsics, width, height)
        intrinsicMesh = new THREE.Mesh(intrinsicRect, fgM)

        const tloader = new THREE.TextureLoader()
        // tloader.setPath(this.furnitureData.pathUrl);
        tloader.load(fgUrl, (fgTex) => {
            // eslint-disable-next-line no-param-reassign
            fgTex.flipY = false
            // eslint-disable-next-line no-param-reassign
            fgTex.encoding = THREE.sRGBEncoding
            // eslint-disable-next-line no-param-reassign
            fgTex.needsUpdate = true
            fgM.map = fgTex
            fgM.opacity = 1.0;
            fgM.needsUpdate = true
        })

        intrinsicMesh.name = 'foreground image'
        return intrinsicMesh
    }

    static createPlaceableOverlay(intrinsics, width, height, maskData, planes, depth) {
        let intrinsicMesh = null
        const depthUniform = {
            factoralpha: { value: new Float32Array([1.0, 0.125]) },
        };
        const material = new THREE.ShaderMaterial({
            uniforms: depthUniform,
            vertexShader: 'varying vec2 vUv;'
            + 'void main() {'
            + '    vUv = uv;'
            + '    vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 );'
            + '    gl_Position = projectionMatrix * mvPosition;'
            + '}',
            fragmentShader: 'uniform vec2 factoralpha;'
            + 'uniform sampler2D map;'
            + 'varying vec2 vUv;'
            + 'void main() {'
            + '    vec4 fetch = texture2D( map, vUv);'
            + '    if(fetch.b < 0.001) discard;'
            + '    if((fetch.b - 0.75) * factoralpha[0] < 0.0) discard;'
            + '    gl_FragColor = vec4(0.0, 1.0, 0.0, factoralpha[1]);'
            + '}',
            transparent: true,
            depthTest: false,
            depthWrite: false,
            side: THREE.DoubleSide,
        });
        const intrinsicRect = WallspiceWebgl.intrinsicsRectGeo(intrinsics, width, height, depth)
        intrinsicMesh = new THREE.Mesh(intrinsicRect, material)

        const pixels = new Uint8Array(maskData.width * maskData.height)
        for (let i = 0; i < maskData.height; i += 1) {
            for (let j = 0; j < maskData.width; j += 1) {
                const index = maskData.data[(i * maskData.width + j) * 4 + 2]
                pixels[i * maskData.width + j] = 0
                if (index > 0) {
                    if (planes[index - 1] && planes[index - 1].class === 'floor') {
                        pixels[i * maskData.width + j] = 128
                    } else {
                        pixels[i * maskData.width + j] = 255
                    }
                }
            }
        }

        const map = new THREE.DataTexture(
            pixels,
            maskData.width,
            maskData.height,
            THREE.LuminanceFormat,
            THREE.UnsignedByteType,
            THREE.UVMapping,
            THREE.ClampToEdgeWrapping,
            THREE.ClampToEdgeWrapping,
            THREE.NearestFilter,
            THREE.NearestFilter,
            8,
        )
        map.flipY = false
        map.needsUpdate = true
        material.uniforms.map = map
        material.uniforms.map.value = map
        material.opacity = 1.0
        material.needsUpdate = true

        return intrinsicMesh
    }

    static setPlaceableAdjust(placeMesh, factor, alpha) {
        if (placeMesh) {
            // eslint-disable-next-line no-param-reassign
            placeMesh.material.uniforms.factoralpha.value = new Float32Array([factor, alpha])
        }
    }

    static isolateChannelDataFromImage(idata, pos) {
        const w = idata.width
        const h = idata.height
        const pixels = new Uint8Array( idata.width * idata.height )

        let segment
        const segments = new Set()

        for(let i = 0; i < w * h; i += 1) {
            segment = idata.data[i * 4 + pos]
            pixels[i] = segment
            if (segment)
                segments.add(segment)
        }
        return {
            w,
            h,
            pixels,
            numSegments: segments.size - 1,
            segments,
        }
    }

    static generateMask(w, h, val) {
        const pixels = new Uint8Array( w * h )

        for(let x = 0; x < w; x += 1) {
            for(let y = 0; y < h; y += 1) {
                pixels[x + y * w] = val
            }
        }

        return {
            w,
            h,
            pixels,
        }
    }

    static keyMask(mask, segments, index, val) {
        for(let x = 0; x < mask.w; x += 1) {
            for(let y = 0; y < mask.h; y += 1) {
                if(segments.pixels[x + y * segments.w] === index)
                // eslint-disable-next-line no-param-reassign
                mask.pixels[x + y * mask.w] = val
            }
        }
    }

    static getValueAtPos(segments, pos) {
        const x = Math.floor((segments.w - 1) * pos[0])
        const y = Math.floor((segments.h - 1) * pos[1])
        const value = segments.pixels[segments.w * y + x]
        return value
    }

    static createLuminanceTextureFromData(maskData) {
        const lTex = new THREE.DataTexture(
            // new Uint8Array(maskData.pixels),
            maskData.pixels,
            maskData.w,
            maskData.h,
            THREE.LuminanceFormat,
            THREE.UnsignedByteType,
            THREE.UVMapping,
            THREE.ClampToEdgeWrapping,
            THREE.ClampToEdgeWrapping,
            THREE.NearestFilter,
            THREE.NearestFilter,
            0,
        )
        lTex.flipY = false
        lTex.needsUpdate = true

        return lTex
    }

    static setSegmentsHighlightIndex(segMesh, index) {
        if (segMesh && segMesh.material) {
            // eslint-disable-next-line no-param-reassign
            segMesh.material.uniforms.idhighlight.value = index / 255.0
        }
    }

    static createSegmentsMesh(intrinsics, width, height, segsTex, depth, isVisible) {
        let intrinsicMesh = null

        const material = new THREE.ShaderMaterial({
            uniforms: {
                idhighlight: { value: 512 / 255.0 }, // out of range (disable)
            },
            vertexShader: 'varying vec2 vUv;'
            + 'void main() {'
            + '    vUv = uv;'
            + '    vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 );'
            + '    gl_Position = projectionMatrix * mvPosition;'
            + '}',
            fragmentShader: 'uniform sampler2D segs;'
            + 'uniform sampler2D mux;'
            + 'uniform float idhighlight;'
            + 'varying vec2 vUv;'
            + 'precision lowp float;'
            + 'void main() {'
            + '    float inc = 1.0/255.0;'
            + '    float alpha = 0.0;'
            + '    float w = 0.0010;'
            + '    float h = 0.0015;'
            + '    vec3 yellow = vec3(1.0, 0.857, 0.003);'
            + '    vec3 blue = vec3(0.0, 0.341, 0.643);'
            + '    vec4 m1 = texture2D( mux, vUv + vec2(-w, -h));'
            + '    vec4 m2 = texture2D( mux, vUv + vec2(w, -h));'
            + '    vec4 m3 = texture2D( mux, vUv + vec2(-w, h));'
            + '    vec4 m4 = texture2D( mux, vUv + vec2(w, h));'
            + '    float mix = max(max(m1.r, m2.r), max(m3.r, m4.r));'
            + '    vec4 p = texture2D( segs, vUv );'
            + '    vec4 p0 = texture2D( segs, vUv + vec2(-w, -h));'
            + '    vec4 p1 = texture2D( segs, vUv + vec2(w, -h));'
            + '    vec4 p2 = texture2D( segs, vUv + vec2(-w, h));'
            + '    vec4 p3 = texture2D( segs, vUv + vec2(w, h));'
            + '    float delta = 100.0 * (abs(p0.r-p1.r) + abs(p0.r-p2.r) + abs(p0.r-p3.r) + abs(p1.r-p2.r));'
            + '    if(p.r == idhighlight) alpha = 0.5;'
            + '    if(delta > 0.1) alpha = 1.0;'
            + '    if(alpha < 0.1) discard;'
            + '    gl_FragColor = vec4(blue*mix+yellow*(1.0-mix), alpha);'
            + '}',
            depthWrite: false,
            transparent: true,
            colorWrite: true,
            side: THREE.DoubleSide,
        });

        const intrinsicRect = WallspiceWebgl.intrinsicsRectGeo(intrinsics, width, height, depth)
        intrinsicMesh = new THREE.Mesh(intrinsicRect, material)

        material.uniforms.segs = segsTex
        material.uniforms.segs.value = segsTex
        material.opacity = 1.0
        material.needsUpdate = true
        material.depthFunc = THREE.AlwaysDepth

        intrinsicMesh.visible = isVisible
        intrinsicMesh.name = 'segments outline and highlight'
        return intrinsicMesh
    }

    static createDebugSegmentsMesh(intrinsics, width, height, segsTex, depth, isVisible) {
        let intrinsicMesh = null

        const material = new THREE.ShaderMaterial({
            vertexShader: 'varying vec2 vUv;'            
            + 'void main() {'
            + '    vUv = uv;'
            + '    vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 );'
            + '    gl_Position = projectionMatrix * mvPosition;'
            + '}',
            fragmentShader: 'uniform sampler2D segs;'
            + 'varying vec2 vUv;'
            + 'precision lowp float;'
            + 'void main() {'
            + '    vec4 normalsSegmenation = texture2D( segs, vUv );'
            + '    float wallIndex = 5.0 + floor((normalsSegmenation.b * 255.0) + 0.5);'
            + '    float r = (mod(wallIndex, 2.0) * 40.0) + (mod(wallIndex, 8.0) * 20.0);'
            + '    float g = (mod(wallIndex, 3.0) * 40.0) + (mod(wallIndex, 7.0) * 20.0);'
            + '    float b = (mod(wallIndex, 4.0) * 40.0) + (mod(wallIndex, 6.0) * 20.0);'
            + '    gl_FragColor = vec4(r / 255.0, g / 255.0, b / 255.0, 0.5);'
            + '}',
            depthWrite: false,
            transparent: true,
            colorWrite: true,
            side: THREE.DoubleSide,
        });

        const intrinsicRect = WallspiceWebgl.intrinsicsRectGeo(intrinsics, width, height, depth)
        intrinsicMesh = new THREE.Mesh(intrinsicRect, material)

        material.uniforms.segs = segsTex
        material.uniforms.segs.value = segsTex
        material.opacity = 1.0
        material.needsUpdate = true
        material.depthFunc = THREE.AlwaysDepth

        intrinsicMesh.visible = isVisible
        intrinsicMesh.name = 'nomrals segmentation visualizer'
        return intrinsicMesh
    }

    static createDepthOcclusionFromImage(intrinsics, width, height, itex, depth, isVisible) {
        let intrinsicMesh = null
        const depthUniform = {
            nearfar: { value: new Float32Array([0.2, 30.0]) },
        };
        const material = new THREE.ShaderMaterial({
            uniforms: depthUniform,
            vertexShader: `
                varying vec2 vUv;
                void main() {
                    vUv = uv;
                    vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 );
                    gl_Position = projectionMatrix * mvPosition;
                }
            `,
            fragmentShader: `
                uniform vec2 nearfar;
                uniform sampler2D map;
                varying vec2 vUv;
                void main() {
                    vec4 fetch = texture2D( map, vUv);
                    if(fetch.a < 0.5) discard; // reject floor fragments
                    // float z = 0.01 * (fetch.r * 255.0 * 256.0 + fetch.g * 255.0); // 16 bit
                    float z = 0.0099609375 * (fetch.r * 65536.0 + fetch.g * 256.0 + fetch.b);
                    float a = nearfar[1] / ( nearfar[1] - nearfar[0] );
                    float b = nearfar[1] * nearfar[0] / ( nearfar[0] - nearfar[1] );
                    gl_FragDepthEXT = ( a + b / z);
                    z = (z - nearfar[0])*0.1;
                    gl_FragColor = vec4(z, z, z, 1.0);
                }
            `,
            depthWrite: true,
            colorWrite: true,
            side: THREE.DoubleSide,
            stencilWrite: true,
            stencilFunc: THREE.NotEqualStencilFunc,
            stencilRef: 2,
        });
        material.extensions.fragDepth = true
        const intrinsicRect = WallspiceWebgl.intrinsicsRectGeo(intrinsics, width, height, depth)
        intrinsicMesh = new THREE.Mesh(intrinsicRect, material)

        const dTex = itex.clone()
        dTex.mapping = THREE.UVMapping
        dTex.wrapS = THREE.ClampToEdgeWrapping
        dTex.wrapT = THREE.ClampToEdgeWrapping
        dTex.magFilter = THREE.NearestFilter
        dTex.minFilter = THREE.NearestFilter
        dTex.anisotropy = 0

        dTex.flipY = false
        dTex.needsUpdate = true
        material.uniforms.map = dTex
        material.uniforms.map.value = dTex
        material.opacity = 1.0
        material.needsUpdate = true

        const depthPackMaterial = new THREE.ShaderMaterial({
            uniforms: depthUniform,
            vertexShader: `
                varying vec2 vUv;
                void main() {
                    vUv = uv;
                    vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 );
                    gl_Position = projectionMatrix * mvPosition;
                }
            `,
            fragmentShader: `
                #include <packing>
                uniform vec2 nearfar;
                uniform sampler2D map;
                varying vec2 vUv;
                void main() {
                    vec4 fetch = texture2D( map, vUv);
                    if(fetch.a < 0.5) discard; // reject floor fragments
                    float z = 0.0099609375 * (fetch.r * 65536.0 + fetch.g * 256.0 + fetch.b);
                    float a = nearfar[1] / ( nearfar[1] - nearfar[0] );
                    float b = nearfar[1] * nearfar[0] / ( nearfar[0] - nearfar[1] );
                    gl_FragColor = packDepthToRGBA(a + b / z);
                }
            `,
            depthWrite: true,
            side: THREE.DoubleSide,
        })
        depthPackMaterial.uniforms.map = dTex
        depthPackMaterial.uniforms.map.value = dTex
        depthPackMaterial.opacity = 1.0
        depthPackMaterial.needsUpdate = true
        intrinsicMesh.depthPackMaterial = depthPackMaterial

        intrinsicMesh.visible = isVisible
        intrinsicMesh.name = 'depth occlusion modifier'
        return intrinsicMesh
    }
    
    static createDepthMuxOcclusionFromImages(intrinsics, width, height, itex1, itex2, depth, isVisible) {
        let intrinsicMesh = null
        const depthUniform = {
            nearfar: { value: new Float32Array([0.2, 30.0]) },
        };
        const material = new THREE.ShaderMaterial({
            uniforms: depthUniform,
            vertexShader: 'varying vec2 vUv;'
            + 'void main() {'
            + '    vUv = uv;'
            + '    vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 );'
            + '    gl_Position = projectionMatrix * mvPosition;'
            + '}',
            fragmentShader: 'uniform vec2 nearfar;'
            + 'uniform sampler2D mux;'
            + 'uniform sampler2D map1;'
            + 'uniform sampler2D map2;'
            + 'varying vec2 vUv;'
            + 'void main() {'
            + '    vec4 fetch;'
            + '    vec4 fetchmask = texture2D( mux, vUv );'
            // + '    if(vUv[0] < 0.5) {'
            + '    if(fetchmask.r < 0.5) {'
            + '        fetch = texture2D( map1, vUv);'
            + '    } else {'
            + '        fetch = texture2D( map2, vUv);'
            + '    }'
            + '    if(fetch.a < 0.5) discard;' // reject floor fragments
            // + '    float z = 0.01 * (fetch.r * 255.0 * 256.0 + fetch.g * 255.0);' // 16 bit
            + '    float z = 0.0099609375 * (fetch.r * 65536.0 + fetch.g * 256.0 + fetch.b);'
            + '    float a = nearfar[1] / ( nearfar[1] - nearfar[0] );'
            + '    float b = nearfar[1] * nearfar[0] / ( nearfar[0] - nearfar[1] );'
            + '    gl_FragDepthEXT = ( a + b / z);'
            // + '    z = (z - nearfar[0])*0.1;'
            + '    gl_FragColor = vec4(z, z, z, 1.0);'
            // + '    gl_FragColor = vec4(fetchmask.r, fetchmask.g, fetchmask.b, 1.0);'
            + '}',
            depthTest: true,
            depthWrite: true,
            colorWrite: false,
            side: THREE.DoubleSide,
        });
        material.extensions.fragDepth = true
        const intrinsicRect = WallspiceWebgl.intrinsicsRectGeo(intrinsics, width, height, depth)
        intrinsicMesh = new THREE.Mesh(intrinsicRect, material)

        const depthPackMaterial = new THREE.ShaderMaterial({
            uniforms: depthUniform,
            vertexShader: `
                varying vec2 vUv;
                void main() {
                    vUv = uv;
                    vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 );
                    gl_Position = projectionMatrix * mvPosition;
                }
            `,
            fragmentShader: `
                #include <packing>
                uniform vec2 nearfar;
                uniform sampler2D mux;
                uniform sampler2D map1;
                uniform sampler2D map2;
                varying vec2 vUv;
                void main() {
                    vec4 fetch;
                    vec4 fetchmask = texture2D( mux, vUv );
                    if(fetchmask.r < 0.5) {
                        fetch = texture2D( map1, vUv);
                    } else {
                        fetch = texture2D( map2, vUv);
                    }
                    if(fetch.a < 0.5) discard; // reject floor fragments
                    float z = 0.0099609375 * (fetch.r * 65536.0 + fetch.g * 256.0 + fetch.b);
                    float a = nearfar[1] / ( nearfar[1] - nearfar[0] );
                    float b = nearfar[1] * nearfar[0] / ( nearfar[0] - nearfar[1] );
                    gl_FragColor = packDepthToRGBA(a + b / z);
                }
            `,
            depthTest: true,
            depthWrite: false,
            side: THREE.FrontSide,
        });

        const dTex1 = itex1.clone()
        dTex1.mapping = THREE.UVMapping
        dTex1.wrapS = THREE.ClampToEdgeWrapping
        dTex1.wrapT = THREE.ClampToEdgeWrapping
        dTex1.magFilter = THREE.NearestFilter
        dTex1.minFilter = THREE.NearestFilter
        dTex1.anisotropy = 0

        dTex1.flipY = false
        dTex1.needsUpdate = true
        material.uniforms.map1 = dTex1
        material.uniforms.map1.value = dTex1

        const dTex2 = itex2.clone()
        dTex2.mapping = THREE.UVMapping
        dTex2.wrapS = THREE.ClampToEdgeWrapping
        dTex2.wrapT = THREE.ClampToEdgeWrapping
        dTex2.magFilter = THREE.NearestFilter
        dTex2.minFilter = THREE.NearestFilter
        dTex2.anisotropy = 0

        dTex2.flipY = false
        dTex2.needsUpdate = true
        material.uniforms.map2 = dTex2
        material.uniforms.map2.value = dTex2

        material.opacity = 1.0
        material.needsUpdate = true

        intrinsicMesh.visible = isVisible
        intrinsicMesh.name = 'depth occlusion modifiermux'

        depthPackMaterial.uniforms.map1 = dTex1
        depthPackMaterial.uniforms.map1.value = dTex1
        depthPackMaterial.uniforms.map2 = dTex2
        depthPackMaterial.uniforms.map2.value = dTex2
        depthPackMaterial.opacity = 1.0
        depthPackMaterial.needsUpdate = true
        intrinsicMesh.depthPackMaterial = depthPackMaterial

        return intrinsicMesh
    }

    static applyMaskTexToShader(tgtmat, tex) {
        // eslint-disable-next-line no-param-reassign
        tgtmat.uniforms.mux = tex
        // eslint-disable-next-line no-param-reassign
        tgtmat.uniforms.mux.value = tex
        // eslint-disable-next-line no-param-reassign
        tgtmat.needsUpdate = true
    }

    static setOcclusionNearFar(depthMesh, near, far) {
        if (depthMesh) {
            // eslint-disable-next-line no-param-reassign
            depthMesh.material.uniforms.nearfar.value = new Float32Array([near, far])
        }
    }

    static createIntrinsicDepthGeom(intrinsics, width, height, idata, floor, textured, offset) {
        const xsize = width / intrinsics[0]
        const ysize = height / intrinsics[1]

        const xoffset = -xsize / 2 + (width * 0.5 - intrinsics[2]) / intrinsics[0]
        const yoffset = -ysize / 2 + (height * 0.5 - intrinsics[3]) / intrinsics[1]

        const vert = []
        const tex = []
        const index = []
        const divx = 600
        const divy = 300
        const incx = 1.0 / divx
        const incy = 1.0 / divy
        let pos = 0
        // let max = 0
        for (let y = 0.0; y <= 1.0001; y += incy) {
            for (let x = 0.0; x <= 1.00001; x += incx) {
                const ypos = Math.floor(y * (idata.height - 1))
                const xpos = Math.floor(x * (idata.width - 1))
                const dpix = (xpos + (ypos * idata.width)) * 4
                const depthi = idata.data[dpix] * 256 * 256 + idata.data[dpix + 1] * 256 + idata.data[dpix + 2]
                const depthm = depthi * 0.0000390625 - offset

                // eslint-disable-next-line no-restricted-globals
                // if (isNaN(depthm)) {
                //     console.log('NaN')
                // }
                vert.push((xoffset + xsize * x) * depthm)
                vert.push(-(yoffset + ysize * y) * depthm)
                vert.push(-depthm)
                tex.push(x)
                tex.push(y)
                if (x < 0.9999 && y < 0.9999 && (floor || idata.data[dpix + 3] > 127)) {
                    index.push(pos, pos + 1, pos + divx + 1)
                    index.push(pos + 1, pos + divx + 2, pos + divx + 1)

                    // const tmp = (pos + 1, pos + divx + 2, pos + divx + 1)
                    // if (tmp > max) {
                    //     max = tmp
                    // }
                }
                pos += 1
            }
        }

        const intrinsicGeom = new THREE.BufferGeometry()
        intrinsicGeom.setAttribute('position', new THREE.Float32BufferAttribute(vert, 3))
        if (textured) {
            intrinsicGeom.setAttribute('uv', new THREE.Float32BufferAttribute(tex, 2))
        }
        intrinsicGeom.setIndex(index)

        return intrinsicGeom
    }

    static createMeshOcclusionFromImage(intrinsics, width, height, idata, isVisible) {
        const material = new THREE.MeshBasicMaterial({ color: 0x000000, side: THREE.DoubleSide })
        material.colorWrite = false

        const intrinsicRect = WallspiceWebgl.createIntrinsicDepthGeom(
            intrinsics, width, height, idata, false, false, 0.01)
        const intrinsicMesh = new THREE.Mesh(intrinsicRect, material)

        intrinsicMesh.name = 'depth occlusion modifier'
        intrinsicMesh.visible = isVisible
        material.needsUpdate = true

        return intrinsicMesh
    }

    static createWall(intrinsics, width, height, fgUrl, depth) {
        let intrinsicMesh = null

        const fgM = new THREE.MeshBasicMaterial({ color: 0xFFFFFF, side: THREE.DoubleSide })
        fgM.transparent = true
        fgM.alphaTest = 0.5
        fgM.opacity = 0.0 // keep invisible until texload lmbda completes
        fgM.depthTest = true
        fgM.depthWrite = true

        // add background to scene
        const intrinsicRect = WallspiceWebgl.intrinsicsRectGeo(intrinsics, width, height, depth)
        intrinsicMesh = new THREE.Mesh(intrinsicRect, fgM)

        const tloader = new THREE.TextureLoader()
        // tloader.setPath(this.furnitureData.pathUrl);
        tloader.load(fgUrl, (fgTex) => {
            // eslint-disable-next-line no-param-reassign
            fgTex.flipY = false
            // eslint-disable-next-line no-param-reassign
            fgTex.needsUpdate = true
            fgM.map = fgTex
            fgM.opacity = 1.0;
            fgM.needsUpdate = true
        })

        intrinsicMesh.name = 'occlusion image'
        return intrinsicMesh
    }

    static createMat4(rotation, position, outMat) {
        tmpXAxis.set(rotation[0][0], rotation[0][1], rotation[0][2])
        tmpYAxis.set(rotation[1][0], rotation[1][1], rotation[1][2])
        tmpZAxis.set(rotation[2][0], rotation[2][1], rotation[2][2])
        tmpMat4.makeBasis(tmpXAxis, tmpYAxis, tmpZAxis)
        tmpMat4.setPosition(new THREE.Vector3(position[0], position[1], position[2]))
        outMat.copy(tmpMat4)
    }

    static unproj(p2, d, fx, fy, cx, cy) {
        return [(p2[0] - cx) / fx * d, -(p2[1] - cy) / fy * d, -d]
    }

    static proj(p3, fx, fy, cx, cy) {
        return [-p3[0] * fx / p3[2] + cx, p3[1] * fy / p3[2] + cy]
    }

    static projBoundBox(objMat, bbox, projMat) {
        const boxCorners = [
            new THREE.Vector3(bbox.min.x, bbox.max.y, bbox.max.z),
            new THREE.Vector3(bbox.min.x, bbox.min.y, bbox.max.z),
            new THREE.Vector3(bbox.min.x, bbox.min.y, bbox.min.z),
            new THREE.Vector3(bbox.min.x, bbox.max.y, bbox.min.z),
            new THREE.Vector3(bbox.max.x, bbox.max.y, bbox.max.z),
            new THREE.Vector3(bbox.max.x, bbox.min.y, bbox.max.z),
            new THREE.Vector3(bbox.max.x, bbox.min.y, bbox.min.z),
            new THREE.Vector3(bbox.max.x, bbox.max.y, bbox.min.z),
        ]

        const screenbox = new THREE.Box3();

        boxCorners.forEach((point) => {
            point.applyMatrix4(objMat)
            point.applyMatrix4(projMat)
            screenbox.expandByPoint(point)
        })

        return [
            [0.5 * (screenbox.min.x + 1), 1.0 - 0.5 * (screenbox.max.y + 1)],
            [0.5 * (screenbox.max.x + 1), 1.0 - 0.5 * (screenbox.max.y + 1)],
            [0.5 * (screenbox.max.x + 1), 1.0 - 0.5 * (screenbox.min.y + 1)],
            [0.5 * (screenbox.min.x + 1), 1.0 - 0.5 * (screenbox.min.y + 1)]]
    }

    static projArtCorners(pose, artSize) {
        const x_axis = pose.rot[0]
        const y_axis = pose.rot[1]
        const P = pose.pos

        const { scale } = pose

        const depth_fx = pose.depth[0]
        const depth_fy = pose.depth[1]
        const depth_cx = pose.depth[2]
        const depth_cy = pose.depth[3]

        const x_step = [0.5 * artSize[0] * x_axis[0], 0.5 * artSize[0] * x_axis[1], 0.5 * artSize[0] * x_axis[2]]
        const y_step = [0.5 * artSize[1] * y_axis[0], 0.5 * artSize[1] * y_axis[1], 0.5 * artSize[1] * y_axis[2]]
        const Q1 = numeric.add(numeric.sub(P, x_step), y_step)
        const Q2 = numeric.add(numeric.add(P, x_step), y_step)
        const Q3 = numeric.sub(numeric.add(P, x_step), y_step)
        const Q4 = numeric.sub(numeric.sub(P, x_step), y_step)

        const q1 = numeric.div(WallspiceWebgl.proj(Q1, depth_fx, depth_fy, depth_cx, depth_cy), scale)
        const q2 = numeric.div(WallspiceWebgl.proj(Q2, depth_fx, depth_fy, depth_cx, depth_cy), scale)
        const q3 = numeric.div(WallspiceWebgl.proj(Q3, depth_fx, depth_fy, depth_cx, depth_cy), scale)
        const q4 = numeric.div(WallspiceWebgl.proj(Q4, depth_fx, depth_fy, depth_cx, depth_cy), scale)

        return [q1, q2, q3, q4]
    }

    static generatePose(intrinsics, wallDimensions, depthImage, mousePosition) {
        const radius = 5
        const depth_fx = intrinsics[0]
        const depth_fy = intrinsics[1]
        const depth_cx = intrinsics[2]
        const depth_cy = intrinsics[3]

        const scale = depthImage.width / wallDimensions[0]
        const pixPos = [Math.floor(mousePosition[0] * scale), Math.floor(mousePosition[1] * scale)]

        const P3 = [[]]
        let count = 0
        let idx = 0
        let mu = [0, 0, 0]
        for (let i = -radius; i <= radius; i += 1) {
            for (let j = -radius; j <= radius; j += 1) {
                const r = i + pixPos[1]
                const c = j + pixPos[0]
                if (i === 0 && j === 0) {
                    idx = count
                }
                if (r >= 0 && r < depthImage.height && c >= 0 && c < depthImage.width) {
                    const pixel_index = r * depthImage.width + c
                    const d = depthImage.data[pixel_index]
                    P3[count] = WallspiceWebgl.unproj([c, r], d, depth_fx, depth_fy, depth_cx, depth_cy)
                    mu = numeric.add(mu, P3[idx])
                    count += 1
                }
            }
        }
        mu = numeric.div(mu, count)
        for (let i = 0; i < count; i += 1) {
            P3[i] = numeric.sub(P3[i], mu)
        }
        // TODO: replace these functions
        // const cov = numeric.dotMMsmall(numeric.transpose(P3), P3)
        // const svdP3 = svd(array2mat(cov), 'full')
        // const vt = transposeMatrix(svdP3.V)
        const vt = null

        if (!vt) {
            return null
        }

        const pos = numeric.add(P3[idx], mu)

        let z = [vt.val[6], vt.val[7], vt.val[8]]
        if (numeric.dotVV(z, pos) > 0) {
            z = [-z[0], -z[1], -z[2]]
        }

        let x = [1, 0, 0]
        let y = [0, 1, 0]
        let normz;
        if (Math.abs(z[0]) > Math.abs(z[1])) {
            z = [z[0], 0, z[2]]
            normz = numeric.norm2(z)
            z = [z[0] / normz, 0, z[2] / normz]
            x = [z[2], 0, -z[0]]
        } else {
            z = [0, z[1], z[2]]
            normz = numeric.norm2(z)
            z = [0, z[1] / normz, z[2] / normz]
            y = [0, z[2], -z[1]]
        }

        const rot = [x, y, z]

        const depth = [
            depth_fx,
            depth_fy,
            depth_cx,
            depth_cy,
        ]

        return {
            rot,
            pos,
            scale,
            depth,
            planeIndex: 0,
        }
    }

    static generatePose24bitCM(intrinsics, planes) {
        const depth_fx = intrinsics[0]
        const depth_fy = intrinsics[1]
        const depth_cx = intrinsics[2]
        const depth_cy = intrinsics[3]

        const pos = [0, -1.1, -1.6]
        // const rot = [[1.0, 0.0, 0.0], [0.0, 0.0, -1.0], [0.0, 1.0, 0.0]]
        const scale = 1.0

        const floor = getFloorPlane(planes)
        const rot = floor.rot
        const depth = [
            depth_fx,
            depth_fy,
            depth_cx,
            depth_cy,
        ]

        return {
            rot,
            pos,
            scale,
            depth,
            planeIndex: 1,
        }
    }

    static generatePoseFromPlanes(intrinsics, planes, maskImage, position, isFurniture, defaultToFloor, isRug, halfHeight = 0.4) {
        if (!maskImage || !maskImage.data) {
            return null
        }
        // planes is the result from the plane equation calc
        // maskImage is in RGBA format
        // example: a 1 in the blue channel indicate wall index 0

        const pixPos = [Math.floor(position[0]), Math.floor(position[1])]
        let plane_index = maskImage.data[pixPos[1] * maskImage.width * 4 + pixPos[0] * 4 + 2]
        // If we're dragging furniture and don't find a plane
        // conditionally use the first floor plane we can find
        if (!plane_index && defaultToFloor && isFurniture) {
            plane_index = getFloorIndex(planes) + 1
        }
        if (!plane_index) {
            return null
        }

        const plane = planes[plane_index - 1]
        if (!plane) {
            return null
        }
        if (isFurniture !== (plane.class === 'floor')) {
            return null
        }
        const ret = WallspiceWebgl.generatePoseFromPlane(plane, position, intrinsics, isRug)
        ret.planeIndex = plane_index - 1

        if(!isFurniture) {
            const floor_index = getFloorIndex(planes)
            const narr = planes[floor_index].rot[2]
            const n = new THREE.Vector3(narr[0], narr[1], narr[2])
            const p = new THREE.Vector3(ret.pos[0], ret.pos[1], ret.pos[2])
            const d = n.dot(p) + planes[floor_index].d
            if(d < halfHeight) return null
        }
        return ret
    }

    static generatePoseFromPlane(plane, position, intrinsics, isRug) {
        const depth_fx = intrinsics[0]
        const depth_fy = intrinsics[1]
        const depth_cx = intrinsics[2]
        const depth_cy = intrinsics[3]

        const z = plane.rot[2]
        const { d } = plane
        const delta = isRug ? 0.0 : RUG_SUB_LAYER_DELTA
        // if (z[2] < 0) {
        //     z = [-z[0], -z[1], -z[2]]
        // }

        let line = WallspiceWebgl.unproj(position, 1.0, depth_fx, depth_fy, depth_cx, depth_cy)
        line = numeric.mul(line, 1.0 / numeric.norm2(line))
        const t = -(d - delta) / numeric.dotVV(z, line)
        const pos = numeric.mul(line, t)

        const x = plane.rot[0]
        const y = plane.rot[1]
        // if (y[1] > 0) {
        //     x = [-x[0], -x[1], -x[2]]
        // } else {
        //     y = [-y[0], -y[1], -y[2]]
        // }

        const rot = [x, y, z];

        const depth = [
            depth_fx,
            depth_fy,
            depth_cx,
            depth_cy,
        ]

        const scale = 1.0

        return {
            rot,
            pos,
            scale,
            depth,
        }
    }

    static findPlaceableWallPositions(wallData, width, height, wallIDs) {
        if (wallData && wallIDs && wallIDs.length > 0) {
            const poss = []
            const midx = Math.floor(wallData.width / 2)
            const midy = Math.floor(wallData.height / 2)
            const starty = midy - 75
            for (let x = midx; x < wallData.width - 100 && x > 100;) {
                for (let y = starty; y < wallData.height - 150 && y > 150;) {
                    const id = wallData.data[(y * wallData.width + x) * 4 + 2]
                    const idoffset = wallData.data[((y + 150) * wallData.width + x) * 4 + 2]
                    if (wallIDs.includes(id) && wallIDs.includes(idoffset)) {
                        poss.push([width * (x / wallData.width), height * (y / wallData.height)])
                    }
                    y = starty - (y - starty)
                    if (y <= starty) {
                        y -= 150
                    }
                }
                x = midx - (x - midx)
                if (x >= midx) {
                    x += 150
                }
            }
            return poss
        }
        return null
    }

    static findPlaceableFloorPositions(occlusionData, width, height) {
        const poss = []

        if (occlusionData) {
            const midx = Math.floor(occlusionData.width / 2)
            const midy = Math.floor(occlusionData.height / 2)
            for (let x = midx; x < occlusionData.width - 100 && x > 100;) {
                for (let y = occlusionData.height - 50; y > midy; y -= 50) {
                    if (occlusionData.data[(y * occlusionData.width + x) * 4 + 3] < 128) {
                        poss.push([width * (x / occlusionData.width), height * (y / occlusionData.height)])
                    }
                }
                x = midx - (x - midx)
                if (x >= midx) {
                    x += 100
                }
            }
        }
        if (poss.length > 1) {
            return poss
        }
        return [[width / 2.0, height - 100.0]]
    }

    static generateLightUvs(wallSize, corners) {
        const uvs = [[], [], [], []]
        for (let i = 0; i < 4; i += 1) {
            uvs[i] = [corners[i][0] / wallSize[0], 1.0 - corners[i][1] / wallSize[1]];
        }
        const lightUvs = new Float32Array([
            uvs[2][0], uvs[2][1],
            uvs[1][0], uvs[1][1],
            uvs[3][0], uvs[3][1],
            uvs[3][0], uvs[3][1],
            uvs[1][0], uvs[1][1],
            uvs[0][0], uvs[0][1],
        ])
        return lightUvs
    }

    static setShadowMeshDirection(shadowMesh, dirVec2) {
        if (shadowMesh) {
            // eslint-disable-next-line no-param-reassign
            shadowMesh.material.uniforms.shadow.value = new Float32Array([dirVec2[0], dirVec2[1]])
        }
    }

    static makeFrameShadow(L, R, B, T, iL, iR, iB, iT, iD, inL, outL) {
        const shadowUniform = {
            shadow: { value: new Float32Array([1, -1.0]) },
        };
        const material = new THREE.ShaderMaterial({
            uniforms: shadowUniform,
            vertexShader: 'uniform vec2 shadow;'
                            + 'attribute float alpha;'
                            + 'attribute float distance;'
                            + 'attribute float spread;'
                            + 'varying float vAlpha;'
                            + 'void main() {'
                            + '   vAlpha = alpha;'
                            + '   vec2 offset = vec2(-shadow[1], shadow[0]) * spread;'
                            + '   vec3 displaced = position + vec3(offset + shadow * distance, 0.0);'
                            + '   vec4 mvPosition = modelViewMatrix * vec4( displaced, 1.0 );'
                            + '   gl_PointSize = 8.0;'
                            + '   gl_Position = projectionMatrix * mvPosition;'
                            + '}',
            fragmentShader: 'varying float vAlpha;'
                            + 'void main() {'
                            + 'gl_FragColor = vec4( 0.0, 0.0, 0.0, vAlpha );'
                            + '}',
            transparent: true,
            depthWrite: false,
            side: THREE.FrontSide,
        });
        const geometry = WallspiceWebgl.makeFrameShadowGeo(L, R, B, T, iL, iR, iB, iT, iD, inL, outL)
        return new THREE.Mesh(geometry, material)
    }

    static makeFrameShadowGeo(L, R, B, T, iL, iR, iB, iT, iD, inL, outL) {
        const distance = 0.02 // AO distance
        const density1 = 0.05 // AO density
        const density2 = 0.3 * 0.2 // shadow density 1/5th there are multiple copies to soften
        const density3 = 0.15 // shadow density inner frame
        const slen = outL // shadow length outer
        const spr = 0.5 // shadow spread factor (corner edge softness with distance)
        const slengthI = inL // shadow length inner
        const offset = 0.001 // zfight offset for inner frame
        const walloffset = 0.007 // zfight offset for wall
        const geo = new THREE.BufferGeometry();
        const vpos = []
        const transp = []
        const extrusion = []
        const ortho = []
        const indices = []

        // ## Positions ##
        // AO halo
        vpos.push(L, T, walloffset)
        vpos.push(L - distance, T + distance, walloffset)
        vpos.push(L, B, 0)
        vpos.push(L - distance, B - distance, walloffset)
        vpos.push(R, B, 0)
        vpos.push(R + distance, B - distance, walloffset)
        vpos.push(R, T, 0)
        vpos.push(R + distance, T + distance, walloffset)

        // 5x Outer shadow
        for (let i = 0; i < 5; i += 1) {
            vpos.push(L, T, walloffset)
            vpos.push(L, T, walloffset)
            vpos.push(L, B, walloffset)
            vpos.push(L, B, walloffset)
            vpos.push(R, B, walloffset)
            vpos.push(R, B, walloffset)
            vpos.push(R, T, walloffset)
            vpos.push(R, T, walloffset)
        }

        // Inner shadow
        vpos.push(iL, iT, iD + offset)
        vpos.push(iL, iT, iD + offset)
        vpos.push(iL, iB, iD + offset)
        vpos.push(iL, iB, iD + offset)
        vpos.push(iR, iB, iD + offset)
        vpos.push(iR, iB, iD + offset)
        vpos.push(iR, iT, iD + offset)
        vpos.push(iR, iT, iD + offset)

        // ## per vertex alpha (darkness) **
        transp.push(density1, 0, density1, 0, density1, 0, density1, 0) // AO
        transp.push(density2, 0, density2, 0, density2, 0, density2, 0) // 5x outer shadow
        transp.push(density2, 0, density2, 0, density2, 0, density2, 0)
        transp.push(density2, 0, density2, 0, density2, 0, density2, 0)
        transp.push(density2, 0, density2, 0, density2, 0, density2, 0)
        transp.push(density2, 0, density2, 0, density2, 0, density2, 0)
        transp.push(0, density3, 0, density3, 0, density3, 0, density3) // Inner Shadow

        // ## degenerate extrusion magnitude ##
        extrusion.push(0, 0, 0, 0, 0, 0, 0, 0) // AO
        extrusion.push(0, slen, 0, slen, 0, slen, 0, slen) // 5x outer shadow
        extrusion.push(0, slen, 0, slen, 0, slen, 0, slen)
        extrusion.push(0, slen, 0, slen, 0, slen, 0, slen)
        extrusion.push(0, slen, 0, slen, 0, slen, 0, slen)
        extrusion.push(0, slen, 0, slen, 0, slen, 0, slen)
        extrusion.push(slengthI, 0, slengthI, 0, slengthI, 0, slengthI, 0) // Inner Shadow

        // ## degenerate orthogonal magnitude ##
        ortho.push(0, 0, 0, 0, 0, 0, 0, 0) // AO
        ortho.push(0, slen * spr, 0, slen * spr, 0, slen * spr, 0, slen * spr) // 5x outer shadow
        ortho.push(0, slen * spr * 0.5, 0, slen * spr * 0.5, 0, slen * spr * 0.5, 0, slen * spr * 0.5)
        ortho.push(0, 0, 0, 0, 0, 0, 0, 0)
        ortho.push(0, slen * -spr * 0.5, 0, slen * -spr * 0.5, 0, slen * -spr * 0.5, 0, slen * -spr * 0.5)
        ortho.push(0, slen * -spr, 0, slen * -spr, 0, slen * -spr, 0, slen * -spr)
        ortho.push(0, 0, 0, 0, 0, 0, 0, 0) // Inner Shadow

        // ## indices ##
        let count = 0;
        for (let i = 0; i < 7; i += 1) {
            indices.push(count + 0, count + 1, count + 2)
            indices.push(count + 2, count + 1, count + 3)
            indices.push(count + 2, count + 3, count + 4)
            indices.push(count + 4, count + 3, count + 5)
            indices.push(count + 4, count + 5, count + 6)
            indices.push(count + 6, count + 5, count + 7)
            indices.push(count + 6, count + 7, count + 0)
            indices.push(count + 0, count + 7, count + 1)
            count += 8
        }

        geo.setAttribute('position', new THREE.Float32BufferAttribute(vpos, 3));
        geo.setAttribute('alpha', new THREE.Float32BufferAttribute(transp, 1));
        geo.setAttribute('distance', new THREE.Float32BufferAttribute(extrusion, 1));
        geo.setAttribute('spread', new THREE.Float32BufferAttribute(ortho, 1));
        geo.setIndex(indices);

        return geo
    }

    static extrudeFrame(L, R, B, T, depth, uscale, vscale, xSect) {
        const geo = new THREE.BufferGeometry();

        if (xSect.length < 2) {
            return null
        }

        let length = 0;
        let trueLength = 0;
        for (let i = 0; i < xSect.length - 1; i += 1) {
            const v1 = xSect[i]
            const v2 = xSect[i + 1]
            const delta = [v2[0] - v1[0], v2[1] - v1[1]];
            trueLength += Math.sqrt(delta[0] * delta[0] + delta[1] * delta[1])
            length += delta[0];
        }

        const Xlength = (R - L) + length + length
        const Ylength = (T - B) + length + length
        const Xchamf = length / Xlength;
        const Ychamf = length / Ylength;

        let innerV = 0;
        let outerV = 0;
        let innerU = 0;
        let outerU = 0;

        // eslint-disable-next-line no-array-constructor
        const pos = new Array()
        // eslint-disable-next-line no-array-constructor
        const ind = new Array()
        // eslint-disable-next-line no-array-constructor
        const uv = new Array()
        for (let i = 0; i < xSect.length - 1; i += 1) {
            const indexBase = i * 8
            const v1 = xSect[i]
            const v2 = xSect[i + 1]
            innerV = outerV
            innerU = outerU
            const delta = [v2[0] - v1[0], v2[1] - v1[1]]
            outerV += Math.sqrt(delta[0] * delta[0] + delta[1] * delta[1]) / trueLength
            outerU += delta[0] / length

            pos.push(
                L - v1[0], T + v1[0], v1[1] + depth,
                L - v2[0], T + v2[0], v2[1] + depth,
                L - v1[0], B - v1[0], v1[1] + depth,
                L - v2[0], B - v2[0], v2[1] + depth,
                R + v1[0], B - v1[0], v1[1] + depth,
                R + v2[0], B - v2[0], v2[1] + depth,
                R + v1[0], T + v1[0], v1[1] + depth,
                R + v2[0], T + v2[0], v2[1] + depth
            );
            // left
            let voffset = 0
            ind.push(indexBase + 0, indexBase + 1, indexBase + 2)
            ind.push(indexBase + 2, indexBase + 1, indexBase + 3)
            uv.push(
                (1 - Ychamf + innerU * Ychamf) * uscale, innerV * vscale + voffset,
                (1 - Ychamf + outerU * Ychamf) * uscale, outerV * vscale + voffset,
                (Ychamf - Ychamf * innerU) * uscale, innerV * vscale + voffset,
            )
            uv.push(
                (Ychamf - Ychamf * innerU) * uscale, innerV * vscale + voffset,
                (1 - Ychamf + outerU * Ychamf) * uscale, outerV * vscale + voffset,
                (Ychamf - Ychamf * outerU) * uscale, outerV * vscale + voffset,
            )
            // bottom
            voffset += vscale
            while (voffset > 0.99) voffset -= 1.0
            ind.push(indexBase + 2, indexBase + 3, indexBase + 4)
            ind.push(indexBase + 4, indexBase + 3, indexBase + 5)
            uv.push(
                (1 - Xchamf + innerU * Xchamf) * uscale, innerV * vscale + voffset,
                (1 - Xchamf + outerU * Xchamf) * uscale, outerV * vscale + voffset,
                (Xchamf - Xchamf * innerU) * uscale, innerV * vscale + voffset,
            )
            uv.push(
                (Xchamf - Xchamf * innerU) * uscale, innerV * vscale + voffset,
                (1 - Xchamf + outerU * Xchamf) * uscale, outerV * vscale + voffset,
                (Xchamf - Xchamf * outerU) * uscale, outerV * vscale + voffset,
            )
            // right
            voffset += vscale
            while (voffset > 0.99) voffset -= 1.0
            ind.push(indexBase + 4, indexBase + 5, indexBase + 6)
            ind.push(indexBase + 6, indexBase + 5, indexBase + 7)
            uv.push(
                (1 - Ychamf + innerU * Ychamf) * uscale, innerV * vscale + voffset,
                (1 - Ychamf + outerU * Ychamf) * uscale, outerV * vscale + voffset,
                (Ychamf - Ychamf * innerU) * uscale, innerV * vscale + voffset,
            )
            uv.push(
                (Ychamf - Ychamf * innerU) * uscale, innerV * vscale + voffset,
                (1 - Ychamf + outerU * Ychamf) * uscale, outerV * vscale + voffset,
                (Ychamf - Ychamf * outerU) * uscale, outerV * vscale + voffset,
            )
            // top
            voffset += vscale
            while (voffset > 0.99) voffset -= 1.0
            ind.push(indexBase + 6, indexBase + 7, indexBase + 0)
            ind.push(indexBase + 0, indexBase + 7, indexBase + 1)
            uv.push(
                (1 - Xchamf + innerU * Xchamf) * uscale, innerV * vscale + voffset,
                (1 - Xchamf + outerU * Xchamf) * uscale, outerV * vscale + voffset,
                (Xchamf - Xchamf * innerU) * uscale, innerV * vscale + voffset,
            )
            uv.push(
                (Xchamf - Xchamf * innerU) * uscale, innerV * vscale + voffset,
                (1 - Xchamf + outerU * Xchamf) * uscale, outerV * vscale + voffset,
                (Xchamf - Xchamf * outerU) * uscale, outerV * vscale + voffset,
            )
        }

        // eslint-disable-next-line no-array-constructor
        const pos2 = new Array()
        ind.forEach((index) => {
            pos2.push(pos[index * 3])
            pos2.push(pos[index * 3 + 1])
            pos2.push(pos[index * 3 + 2])
        })

        geo.setAttribute('position', new THREE.Float32BufferAttribute(pos2, 3))
        geo.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2))
        // geo.setIndex(ind)
        geo.computeVertexNormals()
        geo.verticesNeedUpdate = true
        geo.uvsNeedUpdate = true
        geo.computeBoundingSphere()

        return geo
    }

    // create canvas of stated width, height, depth and wrap uv around edge depth if wrap is true
    static makeCanvasGeometry(width, height, depth, wrap) {
        const geo = new THREE.BufferGeometry();
        let uL = 0;
        let uR = 1;
        let vB = 0;
        let vT = 1;

        if (wrap) {
            uL = depth / (width + depth * 2);
            uR = 1 - uL;
            vB = depth / (height + depth * 2);
            vT = 1 - vB;
        }

        // eslint-disable-next-line no-array-constructor
        const pos = new Array()
        // eslint-disable-next-line no-array-constructor
        const ind = new Array()
        // eslint-disable-next-line no-array-constructor
        const uv = new Array()

        pos.push(-width * 0.5, height * 0.5, depth)
        pos.push(-width * 0.5, -height * 0.5, depth)
        pos.push(width * 0.5, height * 0.5, depth)
        pos.push(width * 0.5, -height * 0.5, depth)
        pos.push(-width * 0.5, height * 0.5, 0.0)
        pos.push(-width * 0.5, -height * 0.5, 0.0)
        pos.push(width * 0.5, height * 0.5, 0.0)
        pos.push(width * 0.5, -height * 0.5, 0.0)

        uv.push(uL, vT)
        uv.push(uL, vB)
        uv.push(uR, vT)
        uv.push(uR, vB)
        uv.push(0.0, 1.0)
        uv.push(0.0, 0.0)
        uv.push(1.0, 1.0)
        uv.push(1.0, 0.0)

        ind.push(0, 1, 2)
        ind.push(2, 1, 3)
        // left
        ind.push(0, 4, 1)
        ind.push(1, 4, 5)
        // right
        ind.push(3, 7, 2)
        ind.push(2, 7, 6)
        // bottom
        ind.push(1, 5, 3)
        ind.push(3, 5, 7)
        // top
        ind.push(2, 6, 0)
        ind.push(0, 6, 4)

        // must deindex for flat shading normals
        // eslint-disable-next-line no-array-constructor
        const pos2 = new Array()
        // eslint-disable-next-line no-array-constructor
        const uv2 = new Array()
        ind.forEach((index) => {
            pos2.push(pos[index * 3])
            pos2.push(pos[index * 3 + 1])
            pos2.push(pos[index * 3 + 2])
            uv2.push(uv[index * 2])
            uv2.push(uv[index * 2 + 1])
        })

        geo.setAttribute('position', new THREE.Float32BufferAttribute(pos2, 3))
        geo.setAttribute('uv', new THREE.Float32BufferAttribute(uv2, 2))
        geo.computeVertexNormals()
        geo.verticesNeedUpdate = true
        geo.uvsNeedUpdate = true
        geo.computeBoundingSphere()

        return geo
    }

    static createBoundingBox(bbox) {
        const geometry = new THREE.BoxBufferGeometry(
            bbox.max.x - bbox.min.x,
            bbox.max.y - bbox.min.y,
            bbox.max.z - bbox.min.z,
        )
        geometry.translate(
            (bbox.max.x - bbox.min.x) * -0.5 + bbox.max.x,
            (bbox.max.y - bbox.min.y) * -0.5 + bbox.max.y,
            (bbox.max.z - bbox.min.z) * -0.5 + bbox.max.z,
        )

        const edges = new THREE.EdgesGeometry(geometry)
        const line = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({
            color: 0xffdb00,
            transparent: true,
            depthFunc: THREE.AlwaysDepth,
        }))
        return line
    }

    static createBoundingBoxMesh(bbox, scalefactor = 1.0) {
        const shape = new THREE.Shape()

        const r = scalefactor / 2.0
        shape.moveTo(-r, -r)
        shape.lineTo(r, -r)
        shape.lineTo(r, r)
        shape.lineTo(-r, r)
        shape.lineTo(-r, -r)

        const d = scalefactor / 4.0

        const curve = new THREE.CatmullRomCurve3([
            new THREE.Vector3(bbox.min.x + d, bbox.min.y + d, bbox.min.z + d),
            new THREE.Vector3(bbox.min.x + d, bbox.min.y + d, bbox.min.z + d),

            new THREE.Vector3(bbox.max.x - d, bbox.min.y + d, bbox.min.z + d),
            new THREE.Vector3(bbox.max.x - d, bbox.min.y + d, bbox.min.z + d),

            new THREE.Vector3(bbox.max.x - d, bbox.max.y - d, bbox.min.z + d),
            new THREE.Vector3(bbox.max.x - d, bbox.max.y - d, bbox.min.z + d),

            new THREE.Vector3(bbox.max.x - d, bbox.max.y - d, bbox.max.z - d),
            new THREE.Vector3(bbox.max.x - d, bbox.max.y - d, bbox.max.z - d),

            new THREE.Vector3(bbox.max.x - d, bbox.min.y + d, bbox.max.z - d),
            new THREE.Vector3(bbox.max.x - d, bbox.min.y + d, bbox.max.z - d),

            new THREE.Vector3(bbox.min.x + d, bbox.min.y + d, bbox.max.z - d),
            new THREE.Vector3(bbox.min.x + d, bbox.min.y + d, bbox.max.z - d),

            new THREE.Vector3(bbox.min.x + d, bbox.max.y - d, bbox.max.z - d),
            new THREE.Vector3(bbox.min.x + d, bbox.max.y - d, bbox.max.z - d),

            new THREE.Vector3(bbox.min.x + d, bbox.max.y - d, bbox.min.z + d),
            new THREE.Vector3(bbox.min.x + d, bbox.max.y - d, bbox.min.z + d),

            new THREE.Vector3(bbox.max.x - d, bbox.max.y - d, bbox.min.z + d),
            new THREE.Vector3(bbox.max.x - d, bbox.max.y - d, bbox.min.z + d),

            new THREE.Vector3(bbox.max.x - d, bbox.min.y + d, bbox.min.z + d),
            new THREE.Vector3(bbox.max.x - d, bbox.min.y + d, bbox.min.z + d),

            new THREE.Vector3(bbox.max.x - d, bbox.min.y + d, bbox.max.z - d),
            new THREE.Vector3(bbox.max.x - d, bbox.min.y + d, bbox.max.z - d),

            new THREE.Vector3(bbox.max.x - d, bbox.max.y - d, bbox.max.z - d),
            new THREE.Vector3(bbox.max.x - d, bbox.max.y - d, bbox.max.z - d),

            new THREE.Vector3(bbox.min.x + d, bbox.max.y - d, bbox.max.z - d),
            new THREE.Vector3(bbox.min.x + d, bbox.max.y - d, bbox.max.z - d),

            new THREE.Vector3(bbox.min.x + d, bbox.min.y + d, bbox.max.z - d),
            new THREE.Vector3(bbox.min.x + d, bbox.min.y + d, bbox.max.z - d),

            new THREE.Vector3(bbox.min.x + d, bbox.min.y + d, bbox.min.z + d),
            new THREE.Vector3(bbox.min.x + d, bbox.min.y + d, bbox.min.z + d),

            new THREE.Vector3(bbox.min.x + d, bbox.max.y - d, bbox.min.z + d),
            new THREE.Vector3(bbox.min.x + d, bbox.max.y - d, bbox.min.z + d),
        ])
        curve.curveType = 'catmullrom'
        curve.tension = 0.01
        const extrudeSettings = {
            steps: 300, // needs excessive subdivision to reasonably approximate the corners
            extrudePath: curve,
        };

        const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings)

        const wirecolor = 0x3bc1c8
        const mat = new THREE.MeshBasicMaterial({
            color: wirecolor, wireframe: false, transparent: true, depthFunc: THREE.AlwaysDepth,
        })
        const mesh = new THREE.Mesh(geometry, mat)
        return mesh
    }

    static cardinalToVector(lightDirection) {
        let xcast;
        let ycast;
        switch (lightDirection) { // convert cardinal direction to shadow displacement
            case 0: // S
                xcast = 0;
                ycast = -1;
                break;
            case 1: // SW
                xcast = -1;
                ycast = -1;
                break;
            case 2: // W
                xcast = -1;
                ycast = 0;
                break;
            case 3: // NW
                xcast = -1;
                ycast = 1;
                break;
            case 4: // N
                xcast = 0;
                ycast = 1;
                break;
            case 5: // NE
                xcast = 1;
                ycast = 1;
                break;
            case 6: // E
                xcast = 1;
                ycast = 0;
                break;
            case 7: // SE
                xcast = 1;
                ycast = -1;
                break;
            default:
                xcast = 1
                ycast = -1
        }
        return [xcast, ycast]
    }

    static screenToPlaneWorld(screenPos, plane, intrinsics) {

        const fx = intrinsics[0]
        const fy = intrinsics[1]
        const cx = intrinsics[2]
        const cy = intrinsics[3]

        const d = plane.d
        const zhat = plane.rot[2]

        const line = WallspiceWebgl.unproj(
            screenPos, 1, fx, fy, cx, cy
        )

        const t = -d / (
            + zhat[0] * line[0]
            + zhat[1] * line[1]
            + zhat[2] * line[2]
        )

        return [
            t * line[0],
            t * line[1],
            t * line[2]
        ]
    }

    static worldToPlane(plane, pos) {

        const d = plane.d

        const xh = plane.rot[0]
        const yh = plane.rot[1]
        const zh = plane.rot[2]

        const p0 = [
            -d * zh[0],
            -d * zh[1],
            -d * zh[2]
        ]

        const p = [
            pos[0] - p0[0],
            pos[1] - p0[1],
            pos[2] - p0[2]
        ]

        const px = p[0] * xh[0] + p[1] * xh[1] + p[2] * xh[2]
        const py = p[0] * yh[0] + p[1] * yh[1] + p[2] * yh[2]
        const pz = p[0] * zh[0] + p[1] * zh[1] + p[2] * zh[2]

        return [px, py, pz]
    }

    static planeToWorld(plane, pos) {

        const p = [
            pos[0],
            pos[1],
            0
        ]

        if( pos.length === 3 ) {
            p[2] = pos[2]
        }
        
        const d = plane.d

        const xh = plane.rot[0]
        const yh = plane.rot[1]
        const zh = plane.rot[2]

        const p0 = [
            -d * zh[0],
            -d * zh[1],
            -d * zh[2]
        ]

        return [
            p0[0] + xh[0] * p[0] + yh[0] * p[1] + zh[0] * p[2],
            p0[1] + xh[1] * p[0] + yh[1] * p[1] + zh[1] * p[2],
            p0[2] + xh[2] * p[0] + yh[2] * p[1] + zh[2] * p[2]
        ]
    }
}

// These directions are 'where the light is shining'
export const DirectionVectorMap = {
    NORTH: 4,
    NORTHEAST: 5,
    EAST: 6,
    SOUTHEAST: 7,
    SOUTH: 0,
    SOUTHWEST: 1,
    WEST: 2,
    NORTHWEST: 3,
}

export default WallspiceWebgl
